<template lang="pug">
    div
        v-snackbar(
            v-model='snackbar.show'
            :color='snackbar.color'
            :timeout="snackbar.timeout"
            top
        )
            | {{ snackbar.text }}
            v-btn(dark text @click='snackbar.show = false')
                | Close

        v-row.ma-0(align="center" justify="center")
            v-col.pa-0(cols='1')
                .text-center
                    v-btn(fab dark small color="primary" @click="handleExitCurrentCampaignCustomer(false)")
                        v-icon(x-large) mdi-chevron-left

            v-col.pa-0(cols='11')
                template(v-if="validCurrentCampaignCustomer")
                    EmailScreen(
                        v-if="isCommunicationTypeEmail"
                        :create-agent="createAgent"
                        :exit-force-campaign-customer="handleExitCurrentCampaignCustomer"
                        :force-campaign-customer="currentCampaignCustomer"
                        :organization-users="organizationUsers"
                        :show-snackbar="showSnackbar"
                    )

                    SmsMessageScreen(
                        v-else-if="isCommunicationTypeSmsMessage"
                        :create-agent="createAgent"
                        :exit-force-campaign-customer="handleExitCurrentCampaignCustomer"
                        :force-campaign-customer="currentCampaignCustomer"
                        :organization-users="organizationUsers"
                        :show-snackbar="showSnackbar"
                    )

                    CallScreen(
                        v-else-if="isCommunicationTypePhone"
                        :create-agent="createAgent"
                        :force-campaign-customer="currentCampaignCustomer"
                        :exit-force-campaign-customer="handleExitCurrentCampaignCustomer"
                        :has-logged-in-queues="true"
                        :is-barge="isBarge"
                        :is-call-without-customer="isCallWithoutCustomer"
                        :is-coach="isCoach"
                        :is-listen="isListen"
                        :is-live-dash-inbound-call="isLiveDashInboundCall"
                        :organization-users="organizationUsers"
                        :parent-force-call="currentCall"
                        :parent-force-web-rtc-participant="currentWebRtcParticipant"
                        :parent-force-web-rtc-session="currentWebRtcSession"
                        :show-snackbar="showSnackbar"
                    )

</template>

<script lang="ts">
    import Vue from 'vue';
    import VolieState from '@/store';

    // Components
    import EmailScreen from '@/components/communication_center/email_screen.vue';
    import SmsMessageScreen from '@/components/communication_center/sms_message_screen.vue';
    import CallScreen from '@/components/communication_center/call_screen.vue';

    // Data
    import { CreateAgent, EnrollCampaignCustomer, ExitCurrentCampaignCustomer, GetOrganizationUsers } from "@/data";

    export default Vue.extend({
        data() {
            return {
                organizationUsers: [],
                snackbar: {
                    color: null,
                    show: false,
                    text: null,
                    timeout: 2500,
                },
            }
        },
        methods: {
            createAgent(options) {

                // Set attempt key
                if (this.currentAgent?.agent_id > 0) {
                    options.attempt_key = this.currentAgent.attempt_key;
                }

                CreateAgent(options).then(() => {
                });

                return;
            },
            createAgentForLoggedIn() {
                const options = {
                    status_name: "Logged In",
                }

                this.createAgent(options);
            },
            getOrganizationUsers() {
                GetOrganizationUsers().then((response) => {
                    if (response?.Data?.organization_users?.length > 0) {
                        this.organizationUsers = response.Data.organization_users;
                    }
                });

                return;
            },
            handleExitCurrentCampaignCustomer(skipAgentCreate) {

                // Create agent if necessary
                if (!skipAgentCreate) {
                    this.createAgentForLoggedIn();
                }

                // Only need to run for expiration code 11s (never eligible)
                if (this.currentCampaignCustomer?.expiration_code === 11) {

                    // Handle exit in server
                    ExitCurrentCampaignCustomer((this.currentCampaignCustomer?.campaign_customer_id || 0)).then(() => {
                    });
                }

                // Force end call
                this.parentForceEndCall = true;

                // Exit
                this.exitCurrentCampaignCustomer();
            },
            evaluateAndReenrollCampaignCustomer() {

                // Check if we already have a valid current
                if (this.validCurrentCampaignCustomer) {
                    return;
                }

                // Ensure we are eligible to enroll here
                if (!this.isEligibleToEvaluate) {
                    return;
                }

                // Init - we do not want to fill in "campaign customer ID" here as we don't want to use an existing record
                const options = {
                    campaign_id: this.currentCampaignCustomer.campaign_id,
                    customer_id: this.currentCampaignCustomer.customer_id,
                    expiration_code: 11, // for both sms and email, we want to leave as 11 so they are not in the queue
                    force: true,
                    last_campaign_customer_id: this.currentCampaignCustomer.campaign_customer_id,
                    override_all: false,
                    purpose: this.currentCampaignCustomer.purpose,
                    source: 19, // current campaign customer
                }

                EnrollCampaignCustomer(options).then((response) => {
                    if (response?.Data?.campaign_customer_active?.campaign_customer_active_id > 0) {

                        // Set fields on current cc active
                        this.currentCampaignCustomer.campaign_customer_active_id = response.Data.campaign_customer_active.campaign_customer_active_id;
                        this.currentCampaignCustomer.account_id = response.Data.campaign_customer_active.account_id;
                        this.currentCampaignCustomer.assigned_to = response.Data.campaign_customer_active.assigned_to;
                        this.currentCampaignCustomer.campaign_customer_id = response.Data.campaign_customer_active.campaign_customer_id;
                        this.currentCampaignCustomer.campaign_id = response.Data.campaign_customer_active.campaign_id;
                        this.currentCampaignCustomer.current_scheduled_communication_id = response.Data.campaign_customer_active.current_scheduled_communication_id;
                        this.currentCampaignCustomer.customer_id = response.Data.campaign_customer_active.customer_id;
                        this.currentCampaignCustomer.customer_vehicle_id = response.Data.campaign_customer_active.customer_vehicle_id;
                        this.currentCampaignCustomer.forte_crm_key_id = response.Data.campaign_customer_active.forte_crm_key_id;
                        this.currentCampaignCustomer.gm_ro_bac_is_valid = response.Data.campaign_customer_active.gm_ro_bac_is_valid;
                        this.currentCampaignCustomer.inline_crm_key = response.Data.campaign_customer_active.inline_crm_key;
                        this.currentCampaignCustomer.next_attemptable_id = response.Data.campaign_customer_active.next_attemptable_id;
                        this.currentCampaignCustomer.next_attemptable_type = response.Data.campaign_customer_active.next_attemptable_type;
                        this.currentCampaignCustomer.noticeable_id = response.Data.campaign_customer_active.noticeable_id;
                        this.currentCampaignCustomer.noticeable_type = response.Data.campaign_customer_active.noticeable_type;
                        this.currentCampaignCustomer.organization_id = response.Data.campaign_customer_active.organization_id;
                        this.currentCampaignCustomer.organization_time_zone_id = response.Data.campaign_customer_active.organization_time_zone_id;
                        this.currentCampaignCustomer.account_forte_crm_enabled = response.Data.campaign_customer_active.account_forte_crm_enabled;
                        this.currentCampaignCustomer.account_inline_crm_enabled = response.Data.campaign_customer_active.account_inline_crm_enabled;
                        this.currentCampaignCustomer.campaign_name = response.Data.campaign_customer_active.campaign_name;
                        this.currentCampaignCustomer.enrolled_date = response.Data.campaign_customer_active.enrolled_date;
                        this.currentCampaignCustomer.expiration_code = response.Data.campaign_customer_active.expiration_code;
                        this.currentCampaignCustomer.next_attemptable_value = response.Data.campaign_customer_active.next_attemptable_value;
                        this.currentCampaignCustomer.next_attempt_number = response.Data.campaign_customer_active.next_attempt_number;
                        this.currentCampaignCustomer.organization_name = response.Data.campaign_customer_active.organization_name;
                        this.currentCampaignCustomer.prospect_id = response.Data.campaign_customer_active.prospect_id;
                        this.currentCampaignCustomer.purpose = response.Data.campaign_customer_active.purpose;
                        this.currentCampaignCustomer.status = response.Data.campaign_customer_active.status;
                        this.currentCampaignCustomer.total_communication_dispositions = response.Data.campaign_customer_active.total_communication_dispositions;
                        this.currentCampaignCustomer.mykaarma_enabled = response.Data.campaign_customer_active.mykaarma_enabled;
                        this.currentCampaignCustomer.tekion_enabled = response.Data.campaign_customer_active.tekion_enabled;
                        this.currentCampaignCustomer.toyoguard_enabled = response.Data.campaign_customer_active.toyoguard_enabled;
                        this.currentCampaignCustomer.user_email = response.Data.campaign_customer_active.user_email;
                        this.currentCampaignCustomer.user_first_name = response.Data.campaign_customer_active.user_first_name;
                        this.currentCampaignCustomer.user_last_name = response.Data.campaign_customer_active.user_last_name;
                        this.currentCampaignCustomer.conversation_group = response.Data.campaign_customer_active.conversation_group;
                        this.currentCampaignCustomer.customer = response.Data.campaign_customer_active.customer;
                        this.currentCampaignCustomer.xtime_enabled = response.Data.campaign_customer_active.xtime_enabled;

                    } else if (response?.Error?.length > 0) {
                        this.showSnackbar("error", response?.Error);
                        // todo - handle use case for 'Customer is already in the queue' => make a response to get the active campaign customer for these campaign ID and customer ID and render that
                    }
                });
            },
            showSnackbar(color, text) {
                if (color?.length > 0 && text?.length > 0) {
                    this.snackbar.color = color;
                    this.snackbar.text = text;
                    this.snackbar.show = true;
                }
            },
        },
        computed: {
            currentAgent() {
                return VolieState.currentAgent();
            },
            isCommunicationTypeEmail() {
                return this.currentCampaignCustomer?.purpose === "return_email";
            },
            isCommunicationTypeSmsMessage() {
                return this.currentCampaignCustomer?.purpose === "return_message";
            },
            isCommunicationTypePhone() {
                return this.isCallWithoutCustomer || this.currentCampaignCustomer?.purpose === "default" || this.currentCampaignCustomer?.purpose === "return_call";
            },
            isEligibleToEvaluate() {
                return this.currentCampaignCustomer?.campaign_customer_id > 0 && this.currentCampaignCustomer?.campaign_id > 0 && this.currentCampaignCustomer?.customer_id > 0 && this.currentCampaignCustomer.purpose?.length > 0;
            },
            validCurrentCampaignCustomer() {
                return this.currentCampaignCustomer?.campaign_customer_active_id > 0 || this.isCallWithoutCustomer;
            },
            validWebRtcParticipant() {
                return this.currentWebRtcParticipant?.id > 0 && this.currentWebRtcSession?.id > 0 && this.currentCall?.id > 0;
            },
        },
        components: {
            EmailScreen,
            SmsMessageScreen,
            CallScreen,
        },
        props: {
            currentCampaignCustomer: Object,
            currentCall: Object,
            currentWebRtcParticipant: Object,
            currentWebRtcSession: Object,
            exitCurrentCampaignCustomer: Function,
            isBarge: Boolean,
            isCallWithoutCustomer: Boolean,
            isCoach: Boolean,
            isListen: Boolean,
            isLiveDashInboundCall: Boolean,
        },
        watch: {
        },
        destroyed() {
        },
        created() {
            this.getOrganizationUsers();
            this.evaluateAndReenrollCampaignCustomer();
        },
    });
</script>

<style lang="scss" scoped>
</style>
